import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Check } from 'lucide-react';

/**
 * VariantDisplay Component
 * Renders interactive variant options with selection and direct price updates
 * Supports Master Variant (Tier 1) for image switching
 * 
 * @param {Array} variants - Array of variant objects with options
 * @param {Array} variantCombinations - Array of combination objects with prices/SKU/stock
 * @param {string} title - Title for the variants section
 * @param {boolean} compact - Whether to show in compact mode
 * @param {function} onSelectionChange - Callback when selection changes
 * @param {function} onImageChange - Callback when master variant selection changes (for image switching)
 * @param {number} basePrice - Base price of the product (used for price calculation)
 */
const VariantDisplay = ({ 
  variants, 
  variantCombinations = [],
  title = "Variants", 
  compact = false,
  onSelectionChange,
  onImageChange,
  basePrice = 0
}) => {
  // Find the master variant (for image switching)
  const masterVariant = useMemo(() => 
    variants?.find(v => v.is_master), 
    [variants]
  );

  // Initialize selections based on variants
  const getInitialSelections = (variantsList) => {
    if (!variantsList || variantsList.length === 0) return {};
    
    const initialSelections = {};
    variantsList.forEach((variant, idx) => {
      const variantKey = variant.name || variant.type || `variant_${idx}`;
      const options = variant.options || variant.values || [];
      // Find first in-stock option
      const firstAvailable = options.find(opt => {
        const inventory = opt.inventory;
        return inventory === undefined || inventory === null || inventory > 0;
      });
      if (firstAvailable) {
        initialSelections[variantKey] = {
          optionIndex: options.indexOf(firstAvailable),
          optionName: firstAvailable.name || firstAvailable.value,
          price: firstAvailable.price,
          image: firstAvailable.image || null
        };
      }
    });
    return initialSelections;
  };

  // Track selected option for each variant type
  const [selections, setSelections] = useState(() => getInitialSelections(variants));

  // Get price from variant combinations or calculate from options
  const getPriceForSelection = (currentSelections) => {
    if (!currentSelections || Object.keys(currentSelections).length === 0) {
      return basePrice;
    }

    // First try to find exact combination match
    if (variantCombinations && variantCombinations.length > 0) {
      const combinationKey = Object.values(currentSelections)
        .map(s => s.optionName)
        .join('|');

      const matchingCombo = variantCombinations.find(c =>
        c.combination_key === combinationKey && c.is_available !== false
      );

      if (matchingCombo && matchingCombo.price != null) {
        return matchingCombo.price;
      }

      // iter-213: legacy combos store options as a dict (no
      // combination_key). Match by comparing each selection.
      const matchByDict = variantCombinations.find(c => {
        const opts = c.options || c.option_values;
        if (!opts || typeof opts !== 'object') return false;
        if (c.is_available === false) return false;
        return Object.values(currentSelections).every(sel => {
          return Object.values(opts).includes(sel.optionName);
        });
      });
      if (matchByDict && matchByDict.price != null) {
        return matchByDict.price;
      }
    }

    // Fallback: Calculate from option prices.
    // iter-213: previously skipped the master variant under the
    // assumption it set base price. But for subscriptions where the
    // master variant IS the priced axis (3-day ₹550 / 7-day ₹1100),
    // skipping master means the price never changes. Use master's
    // selected option price as the new base when present, then add
    // non-master adjustments on top.
    let total = basePrice;
    let masterOverride = null;
    Object.entries(currentSelections).forEach(([variantName, selection]) => {
      const variant = variants?.find(v => (v.name || v.type) === variantName);
      if (variant && variant.is_master) {
        const masterPrice = selection.price ?? selection.unitPrice;
        if (masterPrice != null && masterPrice > 0) {
          masterOverride = masterPrice;
        }
      } else if (variant) {
        const adj = selection.price_impact ?? selection.priceImpact ?? 0;
        if (adj) total += adj;
      }
    });
    if (masterOverride != null) total = masterOverride + (total - basePrice);
    return total;
  };

  // Get inventory/availability for current selection
  const getAvailabilityForSelection = (currentSelections) => {
    if (variantCombinations && variantCombinations.length > 0) {
      const combinationKey = Object.values(currentSelections)
        .map(s => s.optionName)
        .join('|');
      
      const matchingCombo = variantCombinations.find(c => 
        c.combination_key === combinationKey
      );
      
      if (matchingCombo) {
        return {
          available: matchingCombo.is_available !== false,
          inventory: matchingCombo.inventory,
          sku: matchingCombo.sku
        };
      }
    }
    return { available: true, inventory: null, sku: null };
  };

  // Calculate total price and notify parent when selections change
  useEffect(() => {
    if (onSelectionChange) {
      // Use variant combinations for price lookup, or calculate from options
      const finalPrice = getPriceForSelection(selections);
      const availability = getAvailabilityForSelection(selections);
      
      // Calculate price adjustment for display
      const priceAdjustment = finalPrice - basePrice;

      onSelectionChange({
        selections,
        variantPrice: finalPrice,
        priceAdjustment: priceAdjustment > 0 ? priceAdjustment : 0,
        hasSelection: Object.keys(selections).length > 0,
        availability,
        combinationKey: Object.values(selections).map(s => s.optionName).join('|')
      });
    }

    // Notify parent about image change if master variant is selected
    if (onImageChange && masterVariant) {
      const masterKey = masterVariant.name || masterVariant.type;
      const masterSelection = selections[masterKey];
      if (masterSelection?.image) {
        onImageChange(masterSelection.image);
      }
    }
  }, [selections]);

  const handleOptionSelect = (variantKey, optionIndex, option, variant) => {
    const inventory = option.inventory;
    const isOutOfStock = inventory !== undefined && inventory !== null && inventory <= 0;
    
    if (isOutOfStock) return;

    const newSelection = {
      optionIndex,
      optionName: option.name || option.value,
      price: option.price,
      price_impact: option.price_impact ?? option.priceImpact ?? 0,
      image: option.image || null
    };

    setSelections(prev => ({
      ...prev,
      [variantKey]: newSelection
    }));

    // If this is a master variant, trigger image change immediately
    if (variant?.is_master && option.image && onImageChange) {
      onImageChange(option.image);
    }
  };

  if (!variants || variants.length === 0) {
    return null;
  }

  return (
    <Card className={compact ? 'border-0 shadow-none' : ''}>
      {!compact && (
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent className={compact ? 'p-0' : ''}>
        <div className="space-y-5">
          {variants.map((variant, idx) => {
            const variantKey = variant.name || variant.type || `variant_${idx}`;
            const variantName = variant.name || variant.type || `Variant ${idx + 1}`;
            const options = variant.options || variant.values || [];
            const selectedOption = selections[variantKey];
            const isMaster = variant.is_master;
            const displayConfig = variant.display_config || 'buttons'; // Default to buttons
            
            // Helper function to render options based on display config
            const renderOptions = () => {
              // Rich Cards Display
              if (displayConfig === 'cards_rich') {
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {options.map((opt, oIdx) => {
                      const optionName = opt.name || opt.value || `Option ${oIdx + 1}`;
                      const priceImpact = opt.price_impact ?? opt.priceImpact ?? opt.price ?? 0;
                      const inventory = opt.inventory;
                      const optImage = opt.image;
                      const isOutOfStock = inventory !== undefined && inventory !== null && inventory <= 0;
                      const isSelected = selectedOption?.optionIndex === oIdx;
                      
                      return (
                        <button
                          key={oIdx}
                          onClick={() => handleOptionSelect(variantKey, oIdx, opt, variant)}
                          disabled={isOutOfStock}
                          className={`relative flex flex-col rounded-lg border-2 transition-all overflow-hidden ${
                            isOutOfStock
                              ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-50'
                              : isSelected
                                ? 'border-gray-900 shadow-lg'
                                : 'border-gray-200 hover:border-gray-400 cursor-pointer'
                          }`}
                        >
                          {isSelected && (
                            <span className="absolute top-2 right-2 bg-gray-900 rounded-full p-1 z-10">
                              <Check className="h-3 w-3 text-white" />
                            </span>
                          )}
                          {/* Image - Fixed 3:4 aspect ratio (75% of card) */}
                          <div className="w-full bg-gray-100" style={{ aspectRatio: '3/4' }}>
                            {optImage ? (
                              <img 
                                src={optImage} 
                                alt={optionName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <span className="text-sm">No Image</span>
                              </div>
                            )}
                          </div>
                          {/* Text - 25% of card, fixed height */}
                          <div className="p-3 bg-white min-h-[60px] flex flex-col justify-center">
                            <span className={`font-medium text-sm block text-center ${isOutOfStock ? 'line-through' : ''}`}>
                              {optionName}
                            </span>
                            {!isMaster && priceImpact !== 0 && (
                              <span className={`text-xs block mt-0.5 text-center ${priceImpact < 0 ? 'text-green-600' : 'text-gray-500'}`}>
                                {priceImpact > 0 ? '+' : ''}₹{typeof priceImpact === 'number' ? priceImpact.toLocaleString() : priceImpact}
                              </span>
                            )}
                            {isOutOfStock && (
                              <span className="text-xs text-red-500 block mt-0.5 text-center">
                                Out of stock
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              }
              
              // Image Swatches Display
              if (displayConfig === 'swatches') {
                return (
                  <div className="flex flex-wrap gap-2">
                    {options.map((opt, oIdx) => {
                      const optionName = opt.name || opt.value || `Option ${oIdx + 1}`;
                      const inventory = opt.inventory;
                      const optImage = opt.image;
                      const isOutOfStock = inventory !== undefined && inventory !== null && inventory <= 0;
                      const isSelected = selectedOption?.optionIndex === oIdx;
                      
                      return (
                        <button
                          key={oIdx}
                          onClick={() => handleOptionSelect(variantKey, oIdx, opt, variant)}
                          disabled={isOutOfStock}
                          title={optionName}
                          className={`relative rounded-full border-2 transition-all w-12 h-12 overflow-hidden ${
                            isOutOfStock
                              ? 'border-gray-200 cursor-not-allowed opacity-50'
                              : isSelected
                                ? 'border-gray-900 shadow-md ring-2 ring-gray-900 ring-offset-2'
                                : 'border-gray-300 hover:border-gray-500 cursor-pointer'
                          }`}
                        >
                          {isSelected && (
                            <span className="absolute inset-0 bg-gray-900/20 flex items-center justify-center">
                              <Check className="h-4 w-4 text-white drop-shadow" />
                            </span>
                          )}
                          {optImage ? (
                            <img 
                              src={optImage} 
                              alt={optionName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                              <span className="text-xs text-gray-500">{optionName.charAt(0)}</span>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              }
              
              // Scrollable Cards Display
              if (displayConfig === 'cards_scroll') {
                return (
                  <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
                    {options.map((opt, oIdx) => {
                      const optionName = opt.name || opt.value || `Option ${oIdx + 1}`;
                      const priceImpact = opt.price_impact ?? opt.priceImpact ?? opt.price ?? 0;
                      const inventory = opt.inventory;
                      const optImage = opt.image;
                      const isOutOfStock = inventory !== undefined && inventory !== null && inventory <= 0;
                      const isSelected = selectedOption?.optionIndex === oIdx;
                      
                      return (
                        <button
                          key={oIdx}
                          onClick={() => handleOptionSelect(variantKey, oIdx, opt, variant)}
                          disabled={isOutOfStock}
                          className={`flex-shrink-0 w-28 snap-start relative flex flex-col rounded-xl border-2 transition-all overflow-hidden ${
                            isOutOfStock
                              ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-50'
                              : isSelected
                                ? 'border-gray-900 shadow-lg'
                                : 'border-gray-200 hover:border-gray-400 cursor-pointer'
                          }`}
                        >
                          {isSelected && (
                            <span className="absolute top-2 right-2 bg-gray-900 rounded-full p-1 z-10">
                              <Check className="h-3 w-3 text-white" />
                            </span>
                          )}
                          {/* Image - Square aspect ratio */}
                          <div className="w-full aspect-square bg-gray-100">
                            {optImage ? (
                              <img 
                                src={optImage} 
                                alt={optionName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <span className="text-xs">No Image</span>
                              </div>
                            )}
                          </div>
                          {/* Text content */}
                          <div className="p-2 bg-white text-center">
                            <span className={`font-medium text-sm block ${isOutOfStock ? 'line-through' : ''}`}>
                              {optionName}
                            </span>
                            {!isMaster && priceImpact !== 0 && (
                              <span className={`text-xs block mt-0.5 ${priceImpact < 0 ? 'text-green-600' : 'text-gray-500'}`}>
                                {priceImpact > 0 ? '+' : ''}₹{typeof priceImpact === 'number' ? priceImpact.toLocaleString() : priceImpact}
                              </span>
                            )}
                            {isOutOfStock && (
                              <span className="text-xs text-red-500 block mt-0.5">Out of stock</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              }
              
              // Tabs with Grid Display
              if (displayConfig === 'tabs_grid') {
                return (
                  <div className="border rounded-xl overflow-hidden">
                    {/* Tab Headers */}
                    <div className="flex bg-gray-100 border-b overflow-x-auto">
                      {options.map((opt, oIdx) => {
                        const optionName = opt.name || opt.value || `Option ${oIdx + 1}`;
                        const isSelected = selectedOption?.optionIndex === oIdx;
                        const isOutOfStock = opt.inventory !== undefined && opt.inventory !== null && opt.inventory <= 0;
                        
                        return (
                          <button
                            key={oIdx}
                            onClick={() => handleOptionSelect(variantKey, oIdx, opt, variant)}
                            disabled={isOutOfStock}
                            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                              isOutOfStock 
                                ? 'text-gray-400 cursor-not-allowed'
                                : isSelected 
                                  ? 'bg-white text-gray-900 border-b-2 border-gray-900' 
                                  : 'text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            {optionName}
                            {isOutOfStock && <span className="text-xs text-red-500 ml-1">(Out)</span>}
                          </button>
                        );
                      })}
                    </div>
                    {/* Selected option content/image */}
                    <div className="p-4 bg-white">
                      {selectedOption && options[selectedOption.optionIndex]?.image ? (
                        <div className="grid grid-cols-2 gap-2">
                          <img 
                            src={options[selectedOption.optionIndex].image} 
                            alt={selectedOption.optionName}
                            className="w-full aspect-square object-cover rounded-lg"
                          />
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <span className="text-lg font-medium">{selectedOption?.optionName || 'Select an option'}</span>
                          {!isMaster && selectedOption?.price_impact !== 0 && selectedOption?.price_impact !== undefined && (
                            <span className={`block mt-1 ${selectedOption.price_impact < 0 ? 'text-green-600' : 'text-gray-500'}`}>
                              {selectedOption.price_impact > 0 ? '+' : ''}₹{selectedOption.price_impact.toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              
              // Default: Buttons Display
              return (
                <div className="flex flex-wrap gap-2">
                  {options.map((opt, oIdx) => {
                    const optionName = opt.name || opt.value || `Option ${oIdx + 1}`;
                    const priceImpact = opt.price_impact ?? opt.priceImpact ?? opt.price ?? 0;
                    const inventory = opt.inventory;
                    const optImage = opt.image;
                    const isOutOfStock = inventory !== undefined && inventory !== null && inventory <= 0;
                    const isSelected = selectedOption?.optionIndex === oIdx;
                    
                    return (
                      <button
                        key={oIdx}
                        onClick={() => handleOptionSelect(variantKey, oIdx, opt, variant)}
                        disabled={isOutOfStock}
                        className={`relative flex flex-col items-center justify-center px-4 py-2 rounded-lg border-2 transition-all text-center min-w-[80px] ${
                          isOutOfStock
                            ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-50'
                            : isSelected
                              ? 'border-gray-900 bg-gray-900 text-white shadow-md'
                              : 'bg-white border-gray-200 hover:border-gray-400 cursor-pointer'
                        }`}
                      >
                        {isSelected && (
                          <span className="absolute -top-1 -right-1 bg-gray-900 rounded-full p-0.5">
                            <Check className="h-3 w-3 text-white" />
                          </span>
                        )}
                        {/* Show thumbnail for master variant options with images */}
                        {isMaster && optImage && (
                          <img 
                            src={optImage} 
                            alt={optionName}
                            className="w-8 h-8 rounded object-cover mb-1"
                          />
                        )}
                        <span className={`font-medium text-sm ${isOutOfStock ? 'line-through' : ''}`}>
                          {optionName}
                        </span>
                        {/* Show price impact for non-master variants */}
                        {!isMaster && priceImpact !== 0 && (
                          <span className={`text-xs mt-0.5 ${
                            isSelected ? 'text-gray-300' : (priceImpact < 0 ? 'text-green-600' : 'text-gray-500')
                          }`}>
                            {priceImpact > 0 ? '+' : ''}₹{typeof priceImpact === 'number' ? priceImpact.toLocaleString() : priceImpact}
                          </span>
                        )}
                        {isOutOfStock && (
                          <span className="text-xs text-red-500 mt-0.5">
                            Out of stock
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            };
            
            return (
              <div key={variant.id || variant.name || `${variant.title || "variant"}-${idx}`}>
                <h4 className="font-medium text-sm text-gray-700 mb-3">
                  {variantName}
                  {isMaster && <span className="text-xs text-indigo-500 ml-2">(Select for Image)</span>}
                  {selectedOption && (
                    <span className="text-gray-500 font-normal ml-2">
                      — {selectedOption.optionName}
                    </span>
                  )}
                </h4>
                {renderOptions()}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default VariantDisplay;
